import * as React from 'react';

import { cn } from '@/libs';

import { Drawer } from '../drawer/drawer.ts';
import {
  cardCloseButtonClassName,
  helpTextClassName,
  helpTitleClassName,
  iconButtonClassName,
} from '../floating/tones.ts';
import { useDevice, useWindowSize } from '../hooks.tsx';
import { Icon } from '../icon/icon.tsx';
import { Popover } from '../popover/popover.ts';

/** What a hint shows once opened. */
export type HintContent = { text: string; imageUrl?: string };

interface HintProps extends HintContent {
  /** The hint's title; also names the default "?" button. */
  title: string;
  /** Opens the hint in place of the "?" button; must be a native `<button>`. */
  trigger?: React.ReactElement;
  /** How the desktop card lines up with its trigger. */
  align?: 'start' | 'center' | 'end';
}

/** Help behind a "?" or a custom trigger: a floating card on desktop, a bottom sheet on mobile. */
export const Hint = ({
  title,
  text,
  imageUrl,
  trigger,
  align = 'start',
}: HintProps) => {
  const [open, setOpen] = React.useState(false);
  const device = useDevice(useWindowSize());

  const opener = trigger ?? (
    <button
      type="button"
      aria-label={`Help: ${title}`}
      className={cn(
        iconButtonClassName,
        'hover:bg-primary-100 active:bg-primary-lightest data-[popup-open]:bg-primary-100',
      )}
    >
      <Icon
        type={open ? 'question-fill' : 'question'}
        size="medium"
        color="var(--color_primary-darker)"
      />
    </button>
  );
  const image = imageUrl ? (
    <img
      className="mt-2.5 block h-auto max-w-full rounded"
      src={imageUrl}
      alt=""
    />
  ) : null;

  if (device === 'mobile') {
    return (
      <Drawer.Root open={open} onOpenChange={setOpen}>
        <Drawer.Trigger render={opener} />
        <Drawer.Content className="flex flex-col items-center px-5 text-center">
          <Drawer.Title className={helpTitleClassName}>{title}</Drawer.Title>
          {image}
          <Drawer.Description className={helpTextClassName}>
            {text}
          </Drawer.Description>
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger render={opener} />
      <Popover.Content side="top" align={align}>
        {/* Level with the title's first line, so only the title needs room for it. */}
        <Popover.Close aria-label="Close" className={cardCloseButtonClassName}>
          <Icon type="x" size="large" color="var(--color_neutral-light)" />
        </Popover.Close>
        {/* Same text column the old help panel used, inside the card's own 410px cap. */}
        <div className="max-w-[340px] px-5">
          <Popover.Title className={cn(helpTitleClassName, 'pr-3')}>
            {title}
          </Popover.Title>
          {image}
          <Popover.Description className={helpTextClassName}>
            {text}
          </Popover.Description>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
